import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, CircularProgress,
  List, ListItem, ListItemText, Divider, Chip, Grid, Pagination,
  Accordion, AccordionSummary, AccordionDetails, Checkbox
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { searchHouseDailyProgram } from '../services/api';
import SearchSkeleton from './SearchSkeleton';
import EmptyState from './EmptyState';
import ErrorState from './ErrorState';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';

export default function HouseDailyProgramSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });
  const [facets, setFacets] = useState({});
  const [activeFilters, setActiveFilters] = useState({});

  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery && urlQuery !== '*') {
      handleSearch(urlQuery);
    } else {
      handleSearch('*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildQueryWithFilters = (baseQuery, filters) => {
    const filterParts = [];
    Object.entries(filters).forEach(([field, values]) => {
      if (values.length > 0) {
        const fieldQuery = values.map(v => `${field}:${v}`).join(' OR ');
        filterParts.push(`(${fieldQuery})`);
      }
    });
    if (filterParts.length === 0) return baseQuery;
    if (baseQuery === '*') return filterParts.join(' AND ');
    return `(${baseQuery}) AND ${filterParts.join(' AND ')}`;
  };

  const handleSearch = async (searchQuery = null, newPage = 1, filters = activeFilters, pageSize = perPage) => {
    const baseQuery = searchQuery !== null ? searchQuery : (query || '*');
    const q = buildQueryWithFilters(baseQuery, filters);
    setLoading(true);
    setError(null);
    try {
      const data = await searchHouseDailyProgram(q, newPage, pageSize);
      setResults(data);
      setPage(newPage);
      if (data.facets) setFacets(data.facets);
      const newParams = { q: baseQuery };
      if (pageSize !== DEFAULT_PAGE_SIZE) newParams.pageSize = pageSize;
      setSearchParams(newParams);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search. Please try again.');
      setResults(null);
    }
    setLoading(false);
    setInitialLoading(false);
  };

  const handleFacetClick = (field, value) => {
    const newFilters = { ...activeFilters };
    if (!newFilters[field]) newFilters[field] = [];
    const valueIndex = newFilters[field].indexOf(value);
    if (valueIndex > -1) {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      if (newFilters[field].length === 0) delete newFilters[field];
    } else {
      newFilters[field].push(value);
    }
    setActiveFilters(newFilters);
    handleSearch(null, 1, newFilters);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    handleSearch(null, 1, {});
  };

  const handlePageChange = (event, newPage) => {
    handleSearch(null, newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    handleSearch(null, 1, activeFilters, newSize);
  };

  const handleReset = () => {
    setQuery('');
    setResults(null);
    setPage(1);
    setActiveFilters({});
    setFacets({});
    setSearchParams({});
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <EventNoteIcon sx={{ color: '#00bcd4', fontSize: 32 }} />
        <Typography variant="h4">House Daily Program</Typography>
      </Box>

      <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Search House of Representatives Daily Programs..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            variant="outlined"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
          />
          <Button
            variant="contained"
            onClick={() => handleSearch()}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            sx={{ borderRadius: 3, px: 4, bgcolor: '#00bcd4', '&:hover': { bgcolor: '#0097a7' } }}
          >
            Search
          </Button>
          <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 3 }}>
            Reset
          </Button>
        </Box>
      </Paper>

      {initialLoading && <SearchSkeleton count={5} showFacets={true} />}

      {!initialLoading && error && (
        <ErrorState message={error} onRetry={() => handleSearch()} themeColor="#00bcd4" />
      )}

      {!initialLoading && !error && results && results.total === 0 && (
        <EmptyState
          query={query}
          type="daily programs"
          onSuggestionClick={(q) => { setQuery(q); handleSearch(q); }}
          onReset={handleReset}
          themeColor="#00bcd4"
        />
      )}

      {!initialLoading && !error && results && results.total > 0 && (
        <Grid container spacing={3}>
          {facets.years && facets.years.length > 0 && (
            <Grid item xs={12} md={3}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>Year</Typography>
                  {Object.keys(activeFilters).length > 0 && (
                    <Button size="small" onClick={clearAllFilters}>Clear</Button>
                  )}
                </Box>
                <List dense sx={{ py: 0 }}>
                  {facets.years.map((bucket) => (
                    <ListItem key={bucket.value} disablePadding>
                      <Checkbox
                        size="small"
                        checked={activeFilters['year']?.includes(String(bucket.value)) || false}
                        onChange={() => handleFacetClick('year', String(bucket.value))}
                      />
                      <ListItemText
                        primary={`${bucket.value} (${bucket.count})`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          )}

          <Grid item xs={12} md={facets.years?.length > 0 ? 9 : 12}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle1">
                    {results.total.toLocaleString()} programs found
                  </Typography>
                  <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                </Box>
                {results.total > perPage && (
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
              <List>
                {results.hits.map((hit, index) => (
                  <React.Fragment key={hit.id}>
                    {index > 0 && <Divider />}
                    <ListItem alignItems="flex-start">
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="resultTitle">
                              {hit.title || 'Daily Program'}
                            </Typography>
                            {hit.has_pdf && (
                              <Chip
                                icon={<PictureAsPdfIcon />}
                                label="PDF"
                                size="small"
                                color="error"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {formatDate(hit.date)}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              {hit.pdf_url && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  href={hit.pdf_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  startIcon={<PictureAsPdfIcon />}
                                >
                                  View PDF
                                </Button>
                              )}
                              {hit.source_url && (
                                <Button
                                  size="small"
                                  href={hit.source_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  startIcon={<OpenInNewIcon />}
                                >
                                  View on ParlInfo
                                </Button>
                              )}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
              {results.total > perPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
